/**
 * Material-flow READ helper — shared by client and server.
 *
 * "How much of `key` does the cube have?", independent of whether
 * RESOURCE_POLICY routes the key to the `cube.resources` scalar bag or to
 * backpack stacks. The routing decision lives in exactly one place
 * (`resource-policy.ts`); every read goes through here.
 *
 * Why this lives in shared/: the server's canonical read (used by craft cost
 * checks, building canAfford, bot supply thresholds) and the client's cost-UI
 * affordability checks (craft menu, tavern shop, Z build menu) MUST agree —
 * otherwise the UI dims/blocks a purchase the server would accept. This was
 * previously copy-pasted in four files (server items/material-flow.ts + three
 * client menus), each comment claiming to "mirror" the others. Now there is one.
 *
 * The WRITE helpers (creditMaterial / consumeMaterial) stay server-only in
 * `server/src/items/material-flow.ts` because they depend on addToBackpack
 * (server inventory mutation); that file re-exports this getMaterial so server
 * call sites importing from "./material-flow" are unaffected.
 *
 * Optional chaining on `resources` / `backpack` is for the CLIENT: before the
 * first Colyseus delta a Cube can exist with those sub-schemas not yet
 * populated. On the server they're always present (createCube seeds them), so
 * the guards are harmless there.
 */
import type { Cube } from "./state";
import { RESOURCE_POLICY } from "./resource-policy";

export function getMaterial(cube: Cube, key: string): number {
  const policy = RESOURCE_POLICY[key];
  if (!policy) return 0;
  if (policy.kind === "scalar") {
    return (cube.resources as unknown as Record<string, number> | undefined)?.[key] ?? 0;
  }
  let total = 0;
  cube.backpack?.forEach((slot) => {
    if (slot.itemId === policy.itemId) total += slot.count;
  });
  return total;
}
